import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, XIcon } from 'lucide-react';
import { LatestProjectCard } from './LatestProjectCard';
import { LatestProjectDetail } from './LatestProjectDetail';
import { LatestProjectsFilterBar } from './LatestProjectsFilterBar';
import { useLatestProjectsDrawer } from './useLatestProjectsDrawer';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { buttonClass } from '../../utils/buttonStyles';
import { EASE_OUT } from '../../utils/motion';
interface LatestProjectsDrawerProps {
  open: boolean;
  onClose: () => void;
}
export function LatestProjectsDrawer({
  open,
  onClose
}: LatestProjectsDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    filter,
    setFilter,
    counts,
    visible,
    selected,
    viewProject,
    backToList,
    scrollRef,
    closeRef,
    backRef
  } = useLatestProjectsDrawer(open, onClose);
  useBodyScrollLock(open);
  useFocusTrap(panelRef, open);
  const hidden = reduceMotion ? {
    opacity: 0
  } : {
    x: '100%'
  };
  const shown = reduceMotion ? {
    opacity: 1
  } : {
    x: 0
  };
  const closeButton = <button ref={closeRef} type="button" onClick={onClose} aria-label="Close latest projects" className="flex h-11 w-11 shrink-0 items-center justify-center text-white transition-colors duration-150 hover:text-brand-light" data-id="element-276" data-mp="1zjuilxmqaiir">
      <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-277" data-mp="28hwq8w0yw9f0" />
    </button>;
  return <AnimatePresence data-id="element-278" data-mp="2db7zf3t952a3">
      {open && <>
          <motion.div key="latest-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: EASE_OUT
      }} onClick={onClose} className="fixed inset-0 z-[70] bg-black/70" aria-hidden="true" data-id="element-279" data-mp="2iac87tc9kxzw" />
          <motion.div key="latest-panel" id="latest-projects-drawer" ref={panelRef} role="dialog" aria-modal="true" aria-label="Latest projects" initial={hidden} animate={shown} exit={hidden} transition={{
        duration: 0.3,
        ease: EASE_OUT
      }} className="fixed inset-y-0 right-0 z-[71] flex w-[95vw] flex-col border-l border-white/10 bg-charcoal-900 shadow-[-24px_0_48px_rgba(0,0,0,0.45)] md:w-[min(640px,90vw)]" data-id="element-280" data-mp="s5z68omyzaau">
            {selected ? <div className="flex items-center justify-between gap-4 border-b border-white/10 px-3 py-2 sm:px-5" data-id="element-281" data-mp="1jvlrniwon50g">
                <button ref={backRef} type="button" onClick={backToList} className="group inline-flex h-11 items-center gap-2 px-2 font-display text-[14px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-150 hover:text-brand-light" data-id="element-282" data-mp="1fw38arkif1zq">
                  <ArrowLeftIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-283" data-mp="3bkuxvpj3u24p" />
                  Back to projects
                </button>
                {closeButton}
              </div> : <header className="border-b border-white/10 px-5 pb-5 pt-4 sm:px-8" data-id="element-284" data-mp="2ntzd3cd2g76y">
                <div className="flex items-start justify-between gap-4" data-id="element-285" data-mp="2suwqzcr1mf2n">
                  <div className="pt-2" data-id="element-286" data-mp="2euc8khp1il9o">
                    <p className="flex items-center gap-3 font-display text-[13px] font-bold uppercase tracking-[0.22em] text-brand-light" data-id="element-287" data-mp="2evnbkfanvozq">
                      <span className="h-[3px] w-8 bg-brand-red" aria-hidden="true" data-id="element-288" data-mp="d2poz17h9sn8" />
                      Liftoff Investments
                    </p>
                    <h2 className="mt-3 font-display text-[36px] font-extrabold uppercase leading-[0.9] text-white sm:text-[44px]" data-id="element-289" data-mp="i0hi1yjuzkt0">
                      Latest projects
                    </h2>
                  </div>
                  <div className="-mr-2" data-id="element-290" data-mp="296nd0ozu1i1o">{closeButton}</div>
                </div>
                <p className="mt-3 max-w-md text-[15px] leading-relaxed text-steel-300" data-id="element-291" data-mp="245g4uots2guh">
                  Explore our recent and ongoing infrastructure, engineering and construction projects across Botswana.
                </p>
                <div className="mt-5" data-id="element-292" data-mp="1v85vxa4fn3sq">
                  <LatestProjectsFilterBar active={filter} counts={counts} onChange={setFilter} data-id="element-293" data-mp="1qeuk0ukrjwty" />
                </div>
              </header>}

            <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" data-id="element-294" data-mp="1le6wiivqty4h">
              <motion.div key={selected ? selected.id : 'list'} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.18,
            ease: EASE_OUT
          }} data-id="element-295" data-mp="1gfc2slw8mp5l">
                {selected ? <LatestProjectDetail project={selected} data-id="element-296" data-mp="3j3bz8ku8koeo" /> : <div className="px-5 py-6 sm:px-8" data-id="element-297" data-mp="3o1vlkmcqs27k">
                    <p className="sr-only" aria-live="polite" data-id="element-298" data-mp="3s4ikf77tyne9">
                      Showing {visible.length} {visible.length === 1 ? 'project' : 'projects'}
                    </p>
                    {visible.length > 0 ? <ul className="space-y-6" data-id="element-299" data-mp="30e5gixtfzwsv">
                        {visible.map(project => <li key={project.id} data-id="element-300" data-mp="34e61z2eyl89f">
                            <LatestProjectCard project={project} onView={viewProject} data-id="element-301" data-mp="34cuxt1hlpyhs" />
                          </li>)}
                      </ul> : <div className="border border-dashed border-white/20 px-6 py-12 text-center" data-id="element-302" data-mp="3e6u143tevobb">
                        <p className="font-display text-[22px] font-bold uppercase text-white" data-id="element-303" data-mp="2gnae2gdmix7g">No projects in this filter</p>
                        <button type="button" onClick={() => setFilter('all')} className={buttonClass('outline', 'sm', 'mt-5')} data-id="element-304" data-mp="3oz3xx6eqh9ey">
                          Show all projects
                        </button>
                      </div>}
                    <Link to="/projects" onClick={onClose} className="mt-8 inline-flex items-center gap-2 font-display text-[14px] font-bold uppercase tracking-[0.1em] text-steel-200 underline-offset-4 hover:text-white hover:underline" data-id="element-305" data-mp="3tqvohgxw3f53">
                      Browse the full project portfolio
                    </Link>
                  </div>}
              </motion.div>
            </div>

            {selected && <div className="border-t border-white/10 bg-charcoal-950 px-5 py-4 sm:px-8" data-id="element-306" data-mp="ucrym5pm8ykr">
                <Link to={`/contact?service=${selected.enquiryService}#enquiry`} onClick={onClose} className={buttonClass('primary', 'md', 'w-full sm:w-auto')} data-id="element-307" data-mp="pdyj67qrqxhy">
                  Discuss a Similar Project
                </Link>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}